import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import es from './es.json';
import en from './en.json';
import de from './de.json';
import fr from './fr.json';
import it from './it.json';
import zh from './zh.json';

export const SUPPORTED_LANGS = ['es', 'en', 'de', 'fr', 'it', 'zh'] as const;
export type Lang = (typeof SUPPORTED_LANGS)[number];
export type LangPref = 'system' | Lang;
export const LANG_KEY = 'facturita.lang';
/** Spanish is the default, not the browser language: the product's market is Ecuador. */
export const DEFAULT_LANG: Lang = 'es';

function toSupported(tag: string | null | undefined): Lang | null {
  const base = (tag ?? '').toLowerCase().split(/[-_]/)[0];
  return (SUPPORTED_LANGS as readonly string[]).includes(base) ? (base as Lang) : null;
}

/** Browser language when supported, otherwise Spanish (the product's home market). */
export function detectSystemLang(): Lang {
  if (typeof navigator === 'undefined') return 'es';
  for (const tag of navigator.languages?.length ? navigator.languages : [navigator.language]) {
    const l = toSupported(tag);
    if (l) return l;
  }
  return 'es';
}

/**
 * Spanish unless the user picked something else in Cuenta → Preferencias. "Follow the browser"
 * is still offered there, but it is an explicit choice now, so it has to be stored as one —
 * an absent key means the default, not "system".
 */
export function getLangPref(): LangPref {
  try {
    const v = localStorage.getItem(LANG_KEY);
    if (v === 'system') return 'system';
    return toSupported(v) ?? DEFAULT_LANG;
  } catch {
    return DEFAULT_LANG;
  }
}

export function setLangPref(pref: LangPref) {
  try {
    if (pref === DEFAULT_LANG) localStorage.removeItem(LANG_KEY);
    else localStorage.setItem(LANG_KEY, pref);
  } catch {
    /* ignore */
  }
  void i18n.changeLanguage(pref === 'system' ? detectSystemLang() : pref);
}

const initial = getLangPref();

void i18n.use(initReactI18next).init({
  resources: { es: { translation: es }, en: { translation: en }, de: { translation: de }, fr: { translation: fr }, it: { translation: it }, zh: { translation: zh } },
  lng: initial === 'system' ? detectSystemLang() : initial,
  fallbackLng: 'es',
  supportedLngs: SUPPORTED_LANGS,
  interpolation: { escapeValue: false },
  returnObjects: true,
});

// Follow live changes to the browser language while on "system".
if (typeof window !== 'undefined') {
  window.addEventListener('languagechange', () => {
    if (getLangPref() === 'system') void i18n.changeLanguage(detectSystemLang());
  });
}

export default i18n;
